import { Button } from '@/ui/design-system/components/ui/button';
import ExtLink from 'components/ExtLink';
import Link from 'components/Link';
import { cn } from '@/ui/design-system/utils/cn';
import { forwardRef } from 'react';
import { withMixpanelClick } from 'utils/app';

const INTENT_TO_VARIANT = {
    primary: 'default',
    secondary: 'outline',
};

const IconButtonInner = forwardRef(
    (
        {
            IconLeft,
            IconRight,
            children,
            isDisabled,
            intent,
            wrapText = false,
            fetching,
            variant,
            size,
            mixpanelContext,
            className,
            Wrapper,
            // Link-specific props (for LinkIconButton/ExtLinkIconButton)
            to,
            smooth,
            href,
            ...props
        },
        ref,
    ) => {
        const resolvedVariant =
            variant || INTENT_TO_VARIANT[intent] || 'outline';
        const classes = cn(wrapText && 'whitespace-normal', className);

        const leftIconEl = IconLeft ? (
            <IconLeft className="!size-[1.2em] shrink-0 opacity-50" />
        ) : undefined;
        const rightIconEl = IconRight ? (
            <IconRight className="!size-[1.2em] shrink-0 opacity-50" />
        ) : undefined;

        // LinkIconButton pattern: render as Link via asChild
        if (Wrapper === 'link' || to) {
            return (
                <Button
                    ref={ref}
                    variant={resolvedVariant}
                    size={size || 'default'}
                    disabled={!!isDisabled}
                    loading={fetching}
                    asChild
                    className={cn('no-underline', classes)}
                    {...props}
                >
                    <Link to={to} smooth={smooth}>
                        {leftIconEl && (
                            <span className="size-4 shrink-0 flex items-center">
                                {leftIconEl}
                            </span>
                        )}
                        {children}
                        {rightIconEl && (
                            <span className="size-4 shrink-0 flex items-center">
                                {rightIconEl}
                            </span>
                        )}
                    </Link>
                </Button>
            );
        }

        // ExtLinkIconButton pattern: render as ExtLink via asChild
        if (Wrapper === 'extlink' || href) {
            return (
                <Button
                    ref={ref}
                    variant={resolvedVariant}
                    size={size || 'default'}
                    disabled={!!isDisabled}
                    loading={fetching}
                    asChild
                    className={cn('no-underline', classes)}
                >
                    <ExtLink
                        href={href}
                        mixpanelContext={mixpanelContext}
                        unstyled
                        {...props}
                    >
                        {leftIconEl && (
                            <span className="size-4 shrink-0 flex items-center">
                                {leftIconEl}
                            </span>
                        )}
                        {children}
                        {rightIconEl && (
                            <span className="size-4 shrink-0 flex items-center">
                                {rightIconEl}
                            </span>
                        )}
                    </ExtLink>
                </Button>
            );
        }

        // Default: regular button
        return (
            <Button
                ref={ref}
                variant={resolvedVariant}
                size={size || 'default'}
                disabled={!!isDisabled}
                loading={fetching}
                leftIcon={leftIconEl}
                rightIcon={rightIconEl}
                className={classes}
                {...props}
            >
                {children}
            </Button>
        );
    },
);

const IconButton = withMixpanelClick(IconButtonInner, 'IconButton');

export const LinkIconButton = forwardRef((props, ref) => (
    <IconButton {...props} Wrapper="link" ref={ref} />
));

export const ExtLinkIconButton = forwardRef((props, ref) => (
    <IconButton {...props} Wrapper="extlink" ref={ref} />
));

export default IconButton;
